import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import Tooltip from '../../components/shared/Tooltip';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import type { User } from '../../types';
import { UserRole } from '../../types';
import FormError from '../../components/shared/FormError';
import { autoCapFirst } from '../../utils/strings';

const ROLE_STYLES: Record<string, string> = {
  SUPER_ADMIN: 'bg-error/10 text-error',
  TENANT_ADMIN: 'bg-primary/10 text-primary',
  SALES_REP: 'bg-secondary/10 text-secondary',
};

const inviteSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email address is required').email('Enter a valid email address'),
  role: z.enum(['TENANT_ADMIN', 'SALES_REP']),
});
type InviteForm = z.infer<typeof inviteSchema>;

interface EditForm {
  firstName: string;
  lastName: string;
  email: string;
  role: 'TENANT_ADMIN' | 'SALES_REP';
}

const PAGE_SIZE = 10;

const TeamPage: React.FC = () => {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [totalActive, setTotalActive] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const [roleFilter, setRoleFilter] = useState<'ALL' | 'SALES_REP' | 'TENANT_ADMIN'>('ALL');
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);

  const {
    register: registerInvite,
    handleSubmit: handleInviteSubmit,
    reset: resetInvite,
    setValue: setInviteValue,
    formState: { errors: inviteErrors },
  } = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { firstName: '', lastName: '', email: '', role: 'SALES_REP' },
  });

  // Edit state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ firstName: '', lastName: '', email: '', role: 'SALES_REP' });
  const [saving, setSaving] = useState(false);

  // Confirm deactivate
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  }, []);

  const fetchUsers = useCallback(async (page: number, role: 'ALL' | 'SALES_REP' | 'TENANT_ADMIN') => {
    try {
      const params: Record<string, unknown> = { page, limit: PAGE_SIZE };
      if (role !== 'ALL') params.role = role;
      const res = await api.get('/users', { params });
      setUsers(res.data.data ?? []);
      setTotal(res.data.meta?.total ?? 0);
      setTotalActive(res.data.meta?.totalActive ?? 0);
      setCurrentPage(page);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(1, roleFilter);
  }, [fetchUsers, roleFilter]);

  const handleInvite = async (data: InviteForm) => {
    setInviting(true);
    try {
      await api.post('/users', {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim().toLowerCase(),
        role: data.role,
      });
      showToast(`Invitation sent to ${data.email}.`, 'success');
      resetInvite();
      setShowInvite(false);
      await fetchUsers(1, roleFilter);
    } catch {
      showToast('Failed to invite user. The email may already be in use.', 'error');
    } finally {
      setInviting(false);
    }
  };

  const startEdit = (user: User) => {
    setEditingUserId(user.id);
    setEditForm({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email,
      role: (user.role === UserRole.TENANT_ADMIN ? 'TENANT_ADMIN' : 'SALES_REP') as EditForm['role'],
    });
  };

  const cancelEdit = () => {
    setEditingUserId(null);
  };

  const handleSaveEdit = async (userId: string) => {
    setSaving(true);
    try {
      await api.patch(`/users/${userId}`, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        email: editForm.email.trim().toLowerCase(),
        role: editForm.role,
      });
      showToast('User updated successfully.', 'success');
      setEditingUserId(null);
      await fetchUsers(currentPage, roleFilter);
    } catch {
      showToast('Failed to update user.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    if (user.isActive) {
      // Show confirm before deactivating
      setConfirmDeactivateId(user.id);
      return;
    }
    // Reactivate directly
    try {
      await api.patch(`/users/${user.id}`, { isActive: true });
      showToast(`${user.firstName ?? user.email} reactivated.`, 'success');
      await fetchUsers(currentPage, roleFilter);
    } catch {
      showToast('Failed to update user status.', 'error');
    }
  };

  const confirmDeactivate = async () => {
    if (!confirmDeactivateId) return;
    try {
      await api.delete(`/users/${confirmDeactivateId}`);
      showToast('User deactivated.', 'success');
      setConfirmDeactivateId(null);
      await fetchUsers(currentPage, roleFilter);
    } catch {
      showToast('Failed to deactivate user.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">Performance Team</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              Manage your dealership team members &middot; <span className="text-slate-900 dark:text-white font-bold">{totalActive} active</span>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
              {(['ALL', 'SALES_REP', 'TENANT_ADMIN'] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRoleFilter(r)}
                  className={`px-4 h-8 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${roleFilter === r ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                >
                  {r === 'ALL' ? 'All' : r === 'SALES_REP' ? 'Sales Rep' : 'Admin'}
                </button>
              ))}
            </div>
          {!forbidden && (
            <button
              onClick={() => { setShowInvite(true); }}
              className="h-11 px-8 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[20px]">person_add</span>
              Invite User
            </button>
          )}
          </div>
        </div>

        {forbidden ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl text-slate-400">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-4xl">lock</span>
            </div>
            <p className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">Admin access required</p>
            <p className="text-sm font-medium mt-2">Contact your dealer admin to view team members.</p>
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
            <span className="text-sm font-semibold text-slate-400 dark:text-slate-600">Syncing performance team...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 border-dashed rounded-3xl text-slate-400">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-4xl">group_off</span>
            </div>
            <p className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">No team members found</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                  <th className="text-left px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">User Details</th>
                  <th className="text-left px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Access Role</th>
                  <th className="text-left px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Account status</th>
                  <th className="text-left px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Last Authentication</th>
                  <th className="text-right px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((user) => (
                  <tr key={user.id} className="group hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                    {editingUserId === user.id ? (
                      /* Inline edit row */
                      <>
                        <td className="px-8 py-4">
                          <div className="flex gap-4">
                            <input
                              type="text"
                              value={editForm.firstName}
                              onChange={(e) => setEditForm({ ...editForm, firstName: autoCapFirst(e.target.value) })}
                              placeholder="First"
                              className="w-32 h-10 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 text-sm font-bold text-slate-900 dark:text-white focus:border-primary outline-none transition-all"
                            />
                            <input
                              type="text"
                              value={editForm.lastName}
                              onChange={(e) => setEditForm({ ...editForm, lastName: autoCapFirst(e.target.value) })}
                              placeholder="Last"
                              className="w-32 h-10 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 text-sm font-bold text-slate-900 dark:text-white focus:border-primary outline-none transition-all"
                            />
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              placeholder="email"
                              className="w-64 h-10 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 text-sm font-bold text-slate-900 dark:text-white focus:border-primary outline-none transition-all"
                            />
                          </div>
                        </td>
                        <td className="px-8 py-4">
                          <select
                            value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value as EditForm['role'] })}
                            className="h-10 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 text-sm font-bold text-slate-900 dark:text-white focus:border-primary outline-none cursor-pointer transition-all"
                          >
                            <option value="SALES_REP">Sales Rep</option>
                            <option value="TENANT_ADMIN">Dealer Admin</option>
                          </select>
                        </td>
                        <td className="px-8 py-4" colSpan={2}>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Editing User Details</p>
                        </td>
                        <td className="px-8 py-4 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <Tooltip content="Save">
                              <button
                                onClick={() => handleSaveEdit(user.id)}
                                disabled={saving}
                                className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-lg shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
                              >
                                {saving ? (
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                                ) : (
                                  <span className="material-symbols-outlined text-[20px]">check</span>
                                )}
                              </button>
                            </Tooltip>
                            <Tooltip content="Cancel">
                              <button
                                onClick={cancelEdit}
                                className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                              >
                                <span className="material-symbols-outlined text-[20px]">close</span>
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </>
                    ) : (
                      /* Normal display row */
                      <>
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary font-bold shadow-sm border border-primary/10 dark:border-primary/20 group-hover:scale-105 transition-transform">
                              {(user.firstName ?? user.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="flex flex-col">
                              {user.firstName && (
                                <span className="text-sm font-bold text-slate-900 dark:text-white">{user.firstName} {user.lastName}</span>
                              )}
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-0.5 truncate max-w-[200px]">{user.email}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${ROLE_STYLES[user.role] ?? 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                            {({ TENANT_ADMIN: 'Account Admin', SALES_REP: 'Sales Rep' } as Record<string, string>)[user.role] ?? (user.role as string).replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-8 py-6">
                          <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-tighter ${user.isActive ? 'text-primary' : 'text-slate-300'}`}>
                            <span className={`w-2 h-2 rounded-full ${user.isActive ? 'bg-primary' : 'bg-slate-200'}`} />
                            {user.isActive ? 'Active' : 'Deactivated'}
                          </div>
                        </td>
                        <td className="px-8 py-6 text-sm font-medium text-slate-400">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex items-center justify-end gap-2 transition-opacity">
                            <Tooltip content="Edit user">
                              <button
                                onClick={() => startEdit(user)}
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 dark:text-slate-700 hover:text-primary dark:hover:text-primary hover:bg-primary/5 transition-all cursor-pointer"
                              >
                                <span className="material-symbols-outlined text-[20px]">edit_note</span>
                              </button>
                            </Tooltip>
                            {user.id !== currentUserId && (
                              <Tooltip content={user.isActive ? 'Deactivate user' : 'Reactivate user'}>
                              <button
                                  onClick={() => handleToggleActive(user)}
                                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer ${user.isActive
                                      ? 'text-slate-300 dark:text-slate-700 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10'
                                      : 'text-slate-300 dark:text-slate-700 hover:text-primary hover:bg-primary/5'
                                    }`}
                                >
                                  <span className="material-symbols-outlined text-[20px]">
                                    {user.isActive ? 'person_off' : 'undo'}
                                  </span>
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-8 py-4 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[10px] font-black tracking-widest uppercase text-slate-400">
                  Page {currentPage} of {totalPages} &middot; {total} members
                </p>
                <div className="flex items-center gap-1">
                  <Tooltip content="First page">
                    <button
                      onClick={() => fetchUsers(1, roleFilter)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                    >
                      <span className="material-symbols-outlined text-[18px]">first_page</span>
                    </button>
                  </Tooltip>
                  <Tooltip content="Previous page">
                    <button
                      onClick={() => fetchUsers(currentPage - 1, roleFilter)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                    >
                      <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                    </button>
                  </Tooltip>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === 'ellipsis' ? (
                        <span key={`e-${idx}`} className="w-9 h-9 flex items-center justify-center text-slate-300 text-sm">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => fetchUsers(p as number, roleFilter)}
                          className={`w-9 h-9 flex items-center justify-center rounded-xl text-[11px] font-black tracking-wider transition-all ${currentPage === p ? 'bg-primary text-white shadow-md shadow-primary/20' : 'text-slate-400 hover:text-primary hover:bg-primary/5'}`}
                        >
                          {p}
                        </button>
                      )
                    )}
                  <Tooltip content="Next page">
                    <button
                      onClick={() => fetchUsers(currentPage + 1, roleFilter)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                    >
                      <span className="material-symbols-outlined text-[18px]">chevron_right</span>
                    </button>
                  </Tooltip>
                  <Tooltip content="Last page">
                    <button
                      onClick={() => fetchUsers(totalPages, roleFilter)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                    >
                      <span className="material-symbols-outlined text-[18px]">last_page</span>
                    </button>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Overlays at page root */}
      {/* Invite Member Modal */}
      {showInvite && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 sm:px-8 sm:pt-8 sm:pb-6 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-4 sm:gap-5 text-left shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner">
                  <span className="material-symbols-outlined text-[28px]">person_add</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">Invite Member</h2>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mt-1">Add intelligence contributors</p>
                </div>
              </div>
              <button 
                onClick={() => { setShowInvite(false); resetInvite(); }}
                className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-all shadow-sm"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>

            <form onSubmit={handleInviteSubmit(handleInvite)} noValidate className="flex flex-col overflow-hidden">
              <div className="px-10 py-10 space-y-6 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">First Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="John"
                      className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:ring-4 outline-none ${
                        inviteErrors.firstName
                          ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5'
                          : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-primary/5'
                      }`}
                      aria-invalid={!!inviteErrors.firstName}
                      {...registerInvite('firstName')}
                      onChange={(e) => setInviteValue('firstName', autoCapFirst(e.target.value), { shouldDirty: true })}
                    />
                    <FormError message={inviteErrors.firstName?.message} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Last Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Doe"
                      className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:ring-4 outline-none ${
                        inviteErrors.lastName
                          ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5'
                          : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-primary/5'
                      }`}
                      aria-invalid={!!inviteErrors.lastName}
                      {...registerInvite('lastName')}
                      onChange={(e) => setInviteValue('lastName', autoCapFirst(e.target.value), { shouldDirty: true })}
                    />
                    <FormError message={inviteErrors.lastName?.message} />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    placeholder="user@dealership.com"
                    className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:ring-4 outline-none ${
                      inviteErrors.email
                        ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5'
                        : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-primary/5'
                    }`}
                    aria-invalid={!!inviteErrors.email}
                    {...registerInvite('email')}
                  />
                  <FormError message={inviteErrors.email?.message} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Assigned Role <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <select
                      className="h-12 w-full rounded-xl border border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 px-6 text-sm font-medium text-slate-900 dark:text-white transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none appearance-none cursor-pointer"
                      {...registerInvite('role')}
                    >
                      <option value="SALES_REP">Sales Representative</option>
                      <option value="TENANT_ADMIN">Dealer Administrator</option>
                    </select>
                  </div>
                </div>

                <div className="p-6 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-2xl flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                    <span className="material-symbols-outlined text-[20px]">info</span>
                  </div>
                  <p className="text-xs font-bold text-blue-700 dark:text-blue-400 leading-relaxed">
                    Credentials will be dispatched automatically via email. 
                    <span className="block mt-1 text-[10px] font-bold uppercase opacity-60">Security reset enforced on initial login.</span>
                  </p>
                </div>
              </div>

              <div className="px-10 py-10 flex items-center justify-end gap-5 border-t border-slate-100 dark:border-slate-800 shrink-0 bg-slate-50/30 dark:bg-slate-900/30">
                <button
                  type="button"
                  onClick={() => { setShowInvite(false); resetInvite(); }}
                  className="px-10 h-12 rounded-xl font-bold border border-red-50 dark:border-red-900/10 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all cursor-pointer text-xs uppercase tracking-widest"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="h-12 px-12 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer whitespace-nowrap text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                >
                  {inviting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-[20px]">send</span>
                      Send Invitation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {confirmDeactivateId && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-10 space-y-8 text-center max-w-md w-full rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="mx-auto p-4 bg-red-50 dark:bg-red-950/30 rounded-full w-fit">
              <span className="material-symbols-outlined text-[32px] text-red-500 dark:text-red-400 block">person_off</span>
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Deactivate Access</h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm leading-relaxed">
                This user will be suspended from the dealership platform.<br />
                <span className="block mt-1 text-[10px] font-bold uppercase opacity-60">Authentication tokens will be revoked immediately.</span>
              </p>
            </div>
            <div className="flex items-center gap-4 pt-4">
              <button
                onClick={() => setConfirmDeactivateId(null)}
                className="flex-1 h-12 rounded-xl font-bold border border-red-50 dark:border-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all cursor-pointer text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeactivate}
                className="flex-1 h-12 rounded-xl font-bold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white shadow-lg shadow-red-500/20 dark:shadow-red-900/40 transition-all cursor-pointer text-xs uppercase tracking-widest"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;
